import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'profileImage';
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB raw file limit
const TARGET_SIZE = 256; // Resize to 256x256 before storing

/**
 * Compress/resize an image file to a small square JPEG for avatar use.
 * Returns a data URL string.
 */
export function compressImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = TARGET_SIZE;
        canvas.height = TARGET_SIZE;
        const ctx = canvas.getContext('2d')!;
        // Center-crop to square
        const minDim = Math.min(img.width, img.height);
        const sx = (img.width - minDim) / 2;
        const sy = (img.height - minDim) / 2;
        ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, TARGET_SIZE, TARGET_SIZE);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
        resolve(dataUrl);
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Custom hook for managing a profile image stored in localStorage as base64.
 *
 * TODO (WAYP-191): Profile images are currently stored in localStorage only,
 * which means they do not persist across devices or browsers. The planned fix
 * (future sprint) is to:
 *   1. Add an `avatarUrl` column to the User model in Prisma.
 *   2. Upload images to S3/object storage via a new /api/users/:id/avatar endpoint.
 *   3. Serve the URL from the API so every client sees the same avatar.
 *   4. Migrate user preferences (theme, locale, etc.) into a `UserPreferences`
 *      table so they also sync across devices.
 *
 * Until that migration lands, the Header falls back to initials-based avatars
 * when no localStorage image is found. Initials are derived from the user's
 * first and last name stored in the auth token, so they work reliably on any
 * device without any local storage dependency.
 */
export function useProfileImage() {
  const [profileImage, setProfileImageState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setProfileImageState(e.newValue);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const setProfileImage = useCallback((dataUrl: string | null) => {
    if (dataUrl) {
      localStorage.setItem(STORAGE_KEY, dataUrl);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    setProfileImageState(dataUrl);
    window.dispatchEvent(new Event('profileImageChanged'));
  }, []);

  useEffect(() => {
    const handler = () => {
      try {
        setProfileImageState(localStorage.getItem(STORAGE_KEY));
      } catch {
        setProfileImageState(null);
      }
    };
    window.addEventListener('profileImageChanged', handler);
    return () => window.removeEventListener('profileImageChanged', handler);
  }, []);

  return { profileImage, setProfileImage, MAX_FILE_SIZE, compressImage };
}
